import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building, User, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';

import { EmployeeList } from '../components/EmployeeList';
import { BankInfoFields } from '../components/BankInfoFields';
import { AddressFields } from '../components/AddressFields';
import { ContactPersonsList } from '../components/ContactPersonsList';
import { useCompanyForm } from '../hooks';

export function ActionCompanyPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    form,
    fields,
    append,
    remove,
    isSubmitting,
    handleSubmit,
    navigateBack,
    id,
    manage,
  } = useCompanyForm();

  const { control } = form;

  return (
    <div className="bg-[#F5F6F7] min-h-screen pb-24">
      <div className="h-[50px] flex items-center justify-between px-4 py-2 border-b border-[#D9D9D9]">
        <p>
          {id ? `${t('actions.edit')} ${t('companyu')}` : t('newCompany')}
        </p>
        <Button onClick={navigateBack} variant="ghost">
          <X />
        </Button>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="container w-3/4 mx-auto mt-9 bg-white px-3 py-4 rounded-sm drop-shadow-sm flex flex-col gap-4"
        >
          {/* Company Type Selection */}
          <div className="flex gap-16 items-center">
            <div>
              <h1 className="text-xl font-semibold">
                {t('contact')} {t('details')}
              </h1>
              <div className="mt-2.5 flex gap-2.5">
                {!id && (
                  <Button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/app/clients/actionPerson${manage ? `?manage=${manage}` : ''}`
                      )
                    }
                    variant="outline"
                    className="text-black hover:bg-button hover:text-white"
                  >
                    <User />
                    {t('naturalPerson')}
                  </Button>
                )}
                <Button
                  type="button"
                  className="bg-[#F0F0F0] border-2 border-button text-black hover:bg-[#F0F0F0]"
                  disabled={!!id}
                >
                  <Building className="text-button" />
                  {t('legalEntity')}
                </Button>
              </div>
            </div>
          </div>

          <Separator className="my-2.5" />

          {/* Company Basic Info */}
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2.5">
              <FormField
                control={control}
                name="name"
                render={({ field }) => (
                  <FormItem className="flex-1 min-w-[150px]">
                    <FormLabel>
                      {t('name')} <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className={
                          form.formState.errors.name ? 'border-red-500' : 'border-input'
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="flex items-center gap-2.5">
              <FormField
                control={control}
                name="inn"
                render={({ field }) => (
                  <FormItem className="flex-1 min-w-[150px]">
                    <FormLabel>
                      {t('inn')} <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="oked"
                render={({ field }) => (
                  <FormItem className="flex-1 min-w-[150px]">
                    <FormLabel>{t('CCEA')}</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <Separator className="my-2.5" />

          {/* Contact Information */}
          <div className="flex flex-col gap-2.5">
            <h1 className="text-xl font-semibold">{t('contactInfo')}</h1>
            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('emailAddress')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('webSite')}</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value ?? ''} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('phoneNumber')} <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('enterPhone')}
                      className={
                        form.formState.errors.phone ? 'border-red-500' : 'border-input'
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <Separator className="my-2.5" />

          <BankInfoFields control={control} />

          <Separator className="my-2.5" />

          {/* Address Information */}
          <div className="flex flex-col gap-2.5">
            <h1 className="text-xl font-semibold">{t('address')}</h1>
            <AddressFields control={control} layout="company" />
          </div>

          <Separator className="my-2.5" />

          {/* Contact Persons - Only for new company */}
          {!id && (
            <ContactPersonsList
              control={control}
              fields={fields}
              onAppend={() => append({ name: '', role: '', phone: '', email: '' })}
              onRemove={remove}
            />
          )}

          {/* Form Actions */}
          <div className="mt-6 flex gap-3 justify-end">
            {id ? (
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('saving') : t('saveChanges')}
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={() => {
                    form.handleSubmit((values) => {
                      handleSubmit(values);
                      form.reset();
                    })();
                  }}
                >
                  {t('saveAndCreateAnother')}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t('saving') : t('actions.save')}
                </Button>
              </>
            )}
          </div>
        </form>
      </Form>

      {/* Employee List - Only show when editing existing company */}
      {id && (
        <div className="container w-3/4 mx-auto mt-6 bg-white rounded-sm drop-shadow-sm">
          <EmployeeList clientId={Number(id)} />
        </div>
      )}
    </div>
  );
}
